"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

function formatInr(amount: number) {
  if (!Number.isFinite(amount) || amount <= 0) return "₹999";
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

export default function HowItWorksClient() {
  const [paidPriceInr, setPaidPriceInr] = useState<number>(999);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/pricing", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { priceInr?: unknown };
        const n = typeof data.priceInr === "number" ? data.priceInr : null;
        if (!cancelled && n != null && Number.isFinite(n) && n > 0) {
          setPaidPriceInr(n);
        }
      } catch {
        // keep default
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const layers = [
    {
      title: "Personality (OCEAN)",
      desc: "Your natural preferences across how you think, feel, and behave—so recommendations match your real style.",
    },
    {
      title: "Interests (RIASEC)",
      desc: "The kinds of tasks you genuinely enjoy, mapped to career environments where you’ll likely thrive.",
    },
    {
      title: "Thinking & problem solving",
      desc: "Cognitive approach and learning tendencies—helping you choose roles that fit how you solve problems.",
    },
    {
      title: "Emotional intelligence (EQ)",
      desc: "Motivation, stress handling, and interpersonal patterns that strongly shape long-term success.",
    },
    {
      title: "Career dialogue (real context)",
      desc: "A guided conversation that turns your aspirations, constraints, and decision style into actionable signals.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-slate-100">
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
            How it works
          </p>
          <h1 className="font-heading text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
            A structured AI career guidance system
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Aptinomic turns your responses into layered insights and generates a premium career report from{" "}
            <span className="text-slate-50 font-semibold">Step 3 in your dashboard</span>. Free users get a teaser; paid unlocks the full
            guidance report.
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              href="/assessment"
              className="inline-flex items-center justify-center rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-900/60 transition hover:bg-indigo-400"
            >
              Start Free Assessment
            </Link>
            <Link
              href="/checkout"
              className="inline-flex items-center justify-center rounded-full border border-slate-600 bg-slate-900/60 px-6 py-3 text-sm font-medium text-slate-200/80 shadow-sm transition hover:border-indigo-400 hover:text-indigo-300"
            >
              Unlock Full Report
            </Link>
          </div>
        </header>

        <section className="mt-10 rounded-3xl border border-slate-800 bg-slate-900/40 p-6 shadow-sm shadow-slate-950/30 sm:p-8">
          <h2 className="font-heading text-2xl font-semibold text-slate-50">
            The flow
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-300">
            Your path moves from a structured assessment to a guided dialogue, then into report generation you control.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "1) Profile + 5-layer assessment",
                desc: "Complete a 15-minute assessment across psychology, interests, thinking style, emotional intelligence, and practical career context.",
              },
              {
                title: "2) Career dialogue",
                desc: "An AI conversation that extracts clarity from your answers—like a mentor probing deeper.",
              },
              {
                title: "3) Generate your report (Dashboard Step 3)",
                desc: "After dialogue insights are ready, click Generate in your dashboard. The layered report is built layer-by-layer on the server.",
              },
              {
                title: "4) View your guidance",
                desc: "Free shows a premium teaser. Paid unlocks the full career guidance report, including skill gaps and a practical roadmap.",
              },
            ].map((s, idx) => (
              <div
                key={s.title}
                className="flex h-full flex-col gap-3 rounded-2xl bg-slate-900/70 p-4 ring-1 ring-slate-800"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-indigo-500/15 text-sm font-semibold text-amber-300">
                    {idx + 1}
                  </div>
                  <h3 className="text-sm font-semibold text-slate-50">
                    {s.title}
                  </h3>
                </div>
                <p className="text-xs leading-relaxed text-slate-300">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-3xl border border-slate-800 bg-slate-900/40 p-6 shadow-sm shadow-slate-950/30 sm:p-8">
          <h2 className="font-heading text-2xl font-semibold text-slate-50">
            What we analyze (5 layers)
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-300">
            The report is grounded in multiple dimensions, not a single quiz score.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {layers.map((l) => (
              <div
                key={l.title}
                className="rounded-2xl bg-slate-900/70 p-4 ring-1 ring-slate-800"
              >
                <h3 className="text-sm font-semibold text-slate-50">
                  {l.title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-300">
                  {l.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-3xl border border-slate-800 bg-slate-900/40 p-6 shadow-sm shadow-slate-950/30 sm:p-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-slate-50">
                Free vs Paid
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-300">
                Keep moving with confidence. Upgrade when you want the full structured guidance.
              </p>
            </div>
            <p className="text-sm font-semibold text-amber-300">
              Paid price: {formatInr(paidPriceInr)}
            </p>
          </div>

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="flex h-full flex-col justify-between rounded-3xl bg-slate-900/70 p-5 ring-1 ring-slate-800">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Free
                </p>
                <h3 className="mt-2 text-sm font-semibold text-slate-50">
                  A premium teaser to reduce uncertainty
                </h3>
                <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                  <li>• Career snapshot from your 5-layer assessment</li>
                  <li>• Limited guidance highlights</li>
                  <li>• A clear next step to decide what to explore</li>
                  <li>• Generate your report teaser from Step 3 in your dashboard</li>
                </ul>
              </div>
              <div className="pt-5">
                <Link
                  href="/assessment"
                  className="inline-flex w-full items-center justify-center rounded-full border border-slate-600 bg-slate-950/50 px-4 py-2.5 text-xs font-semibold text-slate-200/80 transition hover:border-indigo-400 hover:text-indigo-300"
                >
                  Start free assessment
                </Link>
              </div>
            </div>

            <div className="relative flex h-full flex-col justify-between rounded-3xl bg-gradient-to-b from-indigo-500 via-indigo-600 to-indigo-900 p-5 text-white shadow-lg shadow-indigo-900/60">
              <div className="absolute right-4 top-4 rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/90">
                Most complete
              </div>
              <div className="pt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
                  Paid
                </p>
                <h3 className="mt-2 text-sm font-semibold">Full career guidance report</h3>
                <p className="mt-2 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold">{formatInr(paidPriceInr)}</span>
                  <span className="text-xs text-white/80">one-time</span>
                </p>
                <ul className="mt-3 space-y-1.5 text-xs text-white/90">
                  <li>• Full layered report (all sections)</li>
                  <li>• Skill gaps + readiness plan</li>
                  <li>• Practical roadmap and decision guidance</li>
                  <li>• Shareable PDF for parents & mentors</li>
                </ul>
              </div>
              <div className="pt-5">
                <Link
                  href="/checkout"
                  className="inline-flex w-full items-center justify-center rounded-full bg-white px-4 py-2.5 text-center text-xs font-semibold text-slate-950 shadow-sm transition hover:bg-slate-100"
                >
                  Unlock full report
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-10 rounded-3xl border border-slate-800 bg-slate-900/40 p-6 shadow-sm shadow-slate-950/30 sm:p-8">
          <h2 className="font-heading text-2xl font-semibold text-slate-50">
            Why it feels more reliable
          </h2>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">
            <li>• Layered reasoning across five dimensions</li>
            <li>• Career dialogue clarifies your intent and constraints</li>
            <li>• The roadmap is built for real decision points</li>
            <li>• Premium unlocks the full structured guidance and skill plan</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              href="/assessment"
              className="inline-flex items-center justify-center rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-900/60 transition hover:bg-indigo-400"
            >
              Start Free Assessment
            </Link>
            <Link
              href="/checkout"
              className="inline-flex items-center justify-center rounded-full border border-slate-600 bg-slate-900/60 px-6 py-3 text-sm font-medium text-slate-200/80 shadow-sm transition hover:border-indigo-400 hover:text-indigo-300"
            >
              Unlock Full Report
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

